<template>
  <div class="invoice-page">
    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>

    <div class="invoice-content">
      <div class="invoice-main">
        <!-- Header Card -->
        <div class="invoice-header-card">
          <div class="header-top">
            <h2 class="invoice-title">{{ $t("globals.invoice_no") }}: {{ invoice.invoice_no }}</h2>
            <b-dropdown right class="modern-dropdown" :text="$t('globals.more')">
              <b-dropdown-item
                v-if="invoice.paid_at === null && invoice.credit_invoice_no == null"
                @click="setAsPaid"
              >
                {{ $t('globals.mark_as_paid') }}
              </b-dropdown-item>
              <b-dropdown-item
                v-if="invoice.paid_at === null && invoice.credit_invoice_no == null"
                @click="resend"
              >
                {{ $t('globals.resend') }}
              </b-dropdown-item>
              <b-dropdown-item
                v-if="invoice.credit_invoice_no == null && invoice.total_price_ex_vat > 0"
                @click="createCredit"
              >
                {{ $t('globals.credit_this_invoice') }}
              </b-dropdown-item>
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-item @click="reload">
                {{ $t('globals.refresh') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>

          <div class="invoice-details-grid">
            <div class="detail-item">
              <span class="detail-label">{{ $t('globals.customer') }}</span>
              <router-link
                v-if="invoice && invoice.customer"
                class="detail-link"
                :to="{name: 'customer', params:{ id: invoice.customer.id }}"
              >
                {{ invoice.customer.company_name }}
              </router-link>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t('globals.order') }}</span>
              <router-link
                v-if="invoice && invoice.order"
                class="detail-link"
                :to="{name: 'order', params:{ id: invoice.order.id }}"
              >
                {{ invoice.order.order_no }}
              </router-link>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t('globals.payment_type') }}</span>
              <span class="detail-value">{{ $t(`globals.${invoice.payment_type}`) }}</span>
            </div>
          </div>
        </div>
        <!-- Mollie payments -->
        <div v-if="invoice && invoice.mollie_payments && invoice.mollie_payments.length > 0" class="section-card">
          <div class="section-header">
            <h3 class="section-title">{{ $t("globals.mollie_payments") }}</h3>
            <button v-if="getUserBrandPermissions.super_admin" class="terminate-btn" @click="chargeMollieAgain">
              <v-icon name="exclamation-triangle"></v-icon>
              {{ $t('globals.charge_again') }}
            </button>
          </div>
          <div class="mollie-payments-list">
            <div v-for="payment in invoice.mollie_payments" :key="payment.id" class="mollie-payment-item">
              <div class="payment-row">
                <span class="payment-label">ID:</span>
                <span class="payment-value">{{ payment.id }}</span>
              </div>
              <div class="payment-row">
                <span class="payment-label">{{ $t('globals.status') }}:</span>
                <span class="payment-value status-badge" :class="'status-' + (payment.status || 'unknown').toLowerCase()">
                  {{ payment.status }}
                </span>
              </div>
              <div class="payment-row">
                <span class="payment-label">{{ $t('globals.payment_method') }}:</span>
                <span class="payment-value">{{ payment.payment_method }}</span>
              </div>
              <div class="payment-row">
                <span class="payment-label">{{ $t('globals.invoice_type') }}:</span>
                <span class="payment-value">{{ payment.invoice_type }}</span>
              </div>
              <div class="payment-row">
                <span class="payment-label">{{ $t('globals.invoice_no') }}:</span>
                <span class="payment-value">{{ payment.invoice_no }}</span>
              </div>
              <div class="payment-row">
                <span class="payment-label">{{ $t('globals.created_at') }}:</span>
                <span class="payment-value">{{ payment.created_at ? payment.created_at.substring(0, 10) : '' }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Notes -->
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title">{{ $t("globals.internal_notes") }}</h3>
          </div>
          <textarea
            v-model="invoice.internal_notes"
            class="notes-textarea"
            :placeholder="$t('globals.enter_notes')"
            rows="4"
          ></textarea>
          <div class="section-actions">
            <button class="primary-action-btn" @click="saveNote">
              {{ $t("globals.update") }}
            </button>
          </div>
        </div>
        <!-- Audits Section -->
        <div class="section-card">
          <div class="section-header no-border">
            <div class="title-with-badge">
              <h3 class="section-title">{{ $t("globals.audits") }}</h3>
              <span class="count-badge">{{ invoice.audits ? invoice.audits.length : 0 }}</span>
            </div>
            <button class="primary-action-btn" @click="showAuditsModal = true">
              {{ $t("globals.view_audits") }}
            </button>
          </div>
        </div>
      </div>

      <!-- Invoice PDF Preview -->
      <div class="invoice-sidebar">
        <div class="pdf-preview-card">
          <div class="pdf-header">
            <span class="pdf-title">{{ $t('globals.first_page_preview') }}</span>
            <button class="primary-action-btn" @click="openPdf">
              {{ $t('globals.open') }}
            </button>
          </div>
          <div v-if="pdfLoading" class="loading-state">
            <div class="spinner"></div>
            <p>Loading PDF...</p>
          </div>
          <pdf
            v-if="pdfUrl"
            @loaded="pdfLoading = false"
            class="pdf-preview"
            :src="pdfUrl"
          ></pdf>
        </div>
      </div>
    </div>

    <!-- Audits Modal -->
    <div v-if="showAuditsModal" class="modal-overlay" @click.self="showAuditsModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">{{ $t("globals.audits") }}</h3>
          <button class="modal-close" @click="showAuditsModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="table-wrapper">
            <table class="modern-table">
              <thead>
                <tr>
                  <th>{{ $t('globals.action') }}</th>
                  <th>{{ $t('globals.agent') }}</th>
                  <th>{{ $t('globals.created_at') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(audit, index) in invoice.audits" :key="index">
                  <td>{{ audit.action }}</td>
                  <td>{{ audit.agent ? audit.agent.full_name : '-' }}</td>
                  <td>{{ audit.created_at }}</td>
                </tr>
                <tr v-if="!invoice.audits || invoice.audits.length === 0">
                  <td colspan="3" class="empty-state-cell">No audits found</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button class="primary-action-btn" @click="showAuditsModal = false">
            {{ $t("globals.close") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import pdf from 'vue-pdf';

export default {
  components: {
    pdf
  },
  data() {
    return {
      loading: false,
      pdfLoading: false,
      pdfUrl: null,
      showAuditsModal: false,
      invoice: {
        id: 0,
        order:{
          id: 0,
          order_no: null,
        },
        customer: {
          id: null,
          company_name: null,
        },
        audits: [],
      },
      molliePaymentsFields: [
        "id",
        {
          key: "status",
          label: this.$t("globals.status"),
          sortable: true
        },
        {
          key: "payment_method",
          label: this.$t("globals.payment_method"),
          sortable: true
        },
        {
          key: "invoice_type",
          label: this.$t("globals.invoice_type"),
          sortable: true
        },
        {
          key: "invoice_no",
          label: this.$t("globals.invoice_no"),
          sortable: true
        },
        {
          key: "created_at",
          label: this.$t("globals.created_at"),
          sortable: true
        }
      ],
      auditsFields: [
        {
          key: 'action',
          label: this.$t('globals.action'),
        },
        {
          key: 'agent.full_name',
          label: this.$t('globals.agent'),
        },
        {
          key: 'created_at',
          label: this.$t('globals.created_at'),
          sortable: true,
        },
      ],
    };
  },
  methods: {
    ...mapMutations(["setShowLoadingModal"]),
    ...mapActions("axios_mod", ["simpleFetch", "simpleMutation"]),
    fetchInvoice() {
      const data = {
        query: `
          query {
            invoice (id: ${parseInt(this.$route.params.id)}) {
              id
              invoice_no
              internal_notes
              payment_type
              paid_at
              credit_invoice_no
              total_price_ex_vat
              _rowVariant
              order {
                id
                order_no
              }
              customer {
                id
                company_name
              }
              mollie_payments {
                id
                mollie_payment_id
                status
                payment_method
                invoice_type
                invoice_no
                invoice_id
                created_at
                _showDetails
                _rowVariant
                audits {
                  action
                  created_at
                }
              }
              audits {
                agent {
                  full_name
                }
                action
                created_at
              }
            }
          }
        `
      };
      this.loading = true;
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res && res.invoice) {
            this.invoice = res.invoice;
          }
        }).finally(() => { this.loading = false; });
    },
    loadInvoicePdf() {
      this.pdfLoading = true;
      this.pdfUrl = `${this.getBackendUrl}/pdf_invoices/${this.$route.params.id}.pdf?tmpl=0&locale=${this.getLocale}`;
    },
    openPdf() {
      window.open(this.pdfUrl, "_blank");
    },
    createCredit() {
      let data = {
        query: `
          mutation CreditInvoiceCreateFromInvoice ($invoice_id: Int) { 
            res: CreditInvoiceCreateFromInvoice (invoice_id: $invoice_id) 
          } 
        `,
        variables: {
          invoice_id: parseInt(this.$route.params.id),
        },
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.reload();
      });
    },
    chargeMollieAgain() {
      let data = {
        query: `
          mutation InvoiceMollieChargeAgain ($invoice_id: Int!) { 
            res: InvoiceMollieChargeAgain (invoice_id: $invoice_id) 
          } 
        `,
        variables: {
          invoice_id: parseInt(this.$route.params.id),
        },
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.reload();
      });
    },
    setAsPaid() {
      let data = {
        query: `
          mutation { 
            res: InvoiceUpdatePaidAt (invoice_id: ${parseInt(this.$route.params.id)})
          }
        `,
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.reload();
      });
    },
    resend() {
      let data = {
        query: `
          mutation {
            res: InvoiceResend (invoice_id: ${parseInt(this.$route.params.id)})
          }
        `,
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.reload();
      });
    },
    saveNote() {
      let data = {
        query: `
          mutation {
            res: InvoiceUpdateInternalNote(
              invoice_id: ${parseInt(this.$route.params.id)},
              new_notes: "${this.invoice.internal_notes}"
            )
          }
        `,
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.reload();
      });
    },
    rowClicked(item) {
      item._showDetails = !item._showDetails;
      this.invoice.mollie_payments.forEach(payment => {
        if (item.id != payment.id) {
          payment._showDetails = false;
        }
      });
    },
    openMolliePayment(mollie_payment_id) {
      window.open(
        `https://www.mollie.com/dashboard/org_7462221/payments/${mollie_payment_id}`,
        "_blank"
      );
    },
    reload() {
      this.fetchInvoice();
      // this.loadInvoicePdf();
    },
  },
  computed: {
    ...mapGetters(["getBackendUrl", "getLocale", "getUserBrandPermissions"]),
  },
  created() {
    /*
    if (!this.getUserBrandPermissions.order_show) {
      this.$router.push({ name: "dashboard" });
    }
    */
  },
  mounted() {
    this.fetchInvoice();
    this.loadInvoicePdf();
  }
};
</script>

<style scoped>
.invoice-page {
  background-color: #f7f7f7;
  min-height: 100vh;
  padding: 24px;
  position: relative;
}

.invoice-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.invoice-main {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  min-width: 0;
}

/* Header Card */
.invoice-header-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e2e8f0;
}

.invoice-title {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
}

/* Modern Dropdown Styling */
.modern-dropdown ::v-deep .btn {
  background-color: #5469d4;
  border: none;
  padding: 0.625rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 6px;
  text-transform: uppercase;
  color: white;
  transition: background-color 0.2s;
}

.modern-dropdown ::v-deep .btn:hover,
.modern-dropdown ::v-deep .btn:focus,
.modern-dropdown ::v-deep .btn:active {
  background-color: #4353b8;
  border: none;
  box-shadow: none;
}

.modern-dropdown ::v-deep .dropdown-menu {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 0.5rem 0;
}

.modern-dropdown ::v-deep .dropdown-item {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: #495057;
  transition: background-color 0.2s;
}

.modern-dropdown ::v-deep .dropdown-item:hover {
  background-color: #f8f9fa;
  color: #5469d4;
}

.invoice-details-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-label {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-link {
  color: #5469d4;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
}

.detail-link:hover {
  text-decoration: underline;
}

.detail-value {
  font-size: 14px;
  color: #2d3748;
  font-weight: 500;
}

/* Section Cards */
.section-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-header.no-border {
  border-bottom: none !important;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

.title-with-badge {
  display: flex;
  align-items: center;
  gap: 12px;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  background-color: #5469d4;
  color: white;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 600;
}

.table-wrapper {
  overflow-x: auto;
  margin: 0 -24px;
  padding: 0 24px;
}

.section-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Notes Textarea */
.notes-textarea {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 14px;
  font-size: 0.9375rem;
  font-family: inherit;
  resize: vertical;
  background-color: #f7fafc;
  color: #2d3748;
  line-height: 1.5;
  min-height: 120px;
}

.notes-textarea:focus {
  outline: none;
  border-color: #5469d4;
  background-color: white;
}

/* PDF Preview Sidebar */
.invoice-sidebar {
  position: sticky;
  top: 24px;
  height: fit-content;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.sidebar-section {
  margin: 0;
}

/* Mollie Payments List */
.mollie-payments-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mollie-payment-item {
  padding: 16px;
  background-color: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.payment-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  gap: 12px;
}

.payment-row:not(:last-child) {
  border-bottom: 1px solid #e2e8f0;
}

.payment-label {
  font-size: 13px;
  font-weight: 600;
  color: #718096;
  white-space: nowrap;
}

.payment-value {
  font-size: 13px;
  color: #2d3748;
  font-weight: 500;
  text-align: right;
}

.pdf-preview-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.pdf-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
}

.pdf-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.pdf-preview {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

/* Loading States */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #718096;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #5469d4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-state p,
.loading-overlay p {
  margin: 0;
  font-size: 14px;
  color: #4a5568;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 2px solid #e2e8f0;
}

.modal-title {
  font-size: 20px;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 32px;
  line-height: 1;
  color: #718096;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.modal-close:hover {
  background-color: #f7fafc;
  color: #2d3748;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-body .table-wrapper {
  margin: 0;
  padding: 0;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 2px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .invoice-content {
    grid-template-columns: 1fr;
  }

  .invoice-sidebar {
    position: relative;
    top: 0;
  }
}

@media (max-width: 768px) {
  .invoice-page {
    padding: 16px;
  }

  .invoice-header-card,
  .section-card,
  .pdf-preview-card {
    padding: 16px;
  }

  .header-top {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .invoice-details-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
</style>
